import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Bot, Send, TrendingUp } from "lucide-react";
import { Reveal, SectionHeading } from "./shared";

function PhoneMockup() {
  return (
    <div
      data-testid="preview-mobile-app"
      className="w-[240px] rounded-[2.2rem] border border-slate-200 bg-white shadow-[0_30px_70px_-15px_rgba(15,23,42,0.25)] overflow-hidden"
    >
      <div className="flex justify-center pt-2.5 pb-1.5 bg-white">
        <span className="w-20 h-5 rounded-full bg-slate-900" />
      </div>
      <div className="px-5 pb-6 pt-2">
        <p className="text-[10px] text-slate-400">Tuesday, Aug 3</p>
        <p className="font-display font-medium text-base text-[#0F172A] tracking-tight mt-0.5">Hi Emma</p>

        <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#6366F1] p-4 text-white">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-medium opacity-80">Treatment progress</p>
            <TrendingUp className="w-3.5 h-3.5 opacity-80" strokeWidth={2} />
          </div>
          <div className="flex items-end gap-2 mt-2">
            <p className="font-display font-semibold text-3xl">72%</p>
            <p className="text-[10px] opacity-80 pb-1.5">Week 18 of 25</p>
          </div>
          <div className="h-1.5 rounded-full bg-white/25 mt-3 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "72%" }}
              viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
              className="h-full rounded-full bg-white"
            />
          </div>
        </div>

        <div className="mt-3 rounded-2xl border border-slate-100 p-3.5">
          <p className="text-[10px] font-semibold text-[#0F172A]">Next appointment</p>
          <p className="text-[10px] text-slate-400 mt-1">Thu, Aug 6 · 09:00 · Aligner check</p>
        </div>
        <div className="mt-3 rounded-2xl border border-slate-100 p-3.5 flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-[#6366F1]/10 flex items-center justify-center text-[10px] font-bold text-[#6366F1]">SC</span>
          <div>
            <p className="text-[10px] font-semibold text-[#0F172A]">Smile Coach</p>
            <p className="text-[9px] text-slate-400">3-day streak — keep it up</p>
          </div>
        </div>
        <div className="mt-4 rounded-full bg-[#2563EB] text-white text-center text-[10px] font-semibold py-2.5">
          Message your care team
        </div>
      </div>
    </div>
  );
}

function CopilotCard() {
  return (
    <div
      data-testid="preview-ai-copilot"
      className="w-[300px] rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-xl shadow-[0_24px_60px_-12px_rgba(15,23,42,0.2)] p-4"
    >
      <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
        <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" strokeWidth={1.8} />
        </span>
        <div>
          <p className="text-xs font-semibold text-[#0F172A]">Alignexa Copilot</p>
          <p className="text-[9px] text-emerald-500 font-medium">Online</p>
        </div>
      </div>
      <div className="space-y-2.5 pt-3">
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-md bg-[#2563EB] text-white text-[11px] leading-snug px-3.5 py-2.5">
          Summarize Emma's last visit
        </div>
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-slate-100 text-[11px] text-slate-600 leading-snug px-3.5 py-2.5">
          Emma's aligner tracking is on schedule. Mild irritation reported on tray 12 — resolved. Next check in 6 weeks. Note drafted for your approval.
        </div>
        <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3.5 py-2 mt-1">
          <span className="text-[10px] text-slate-400 flex-1">Ask anything…</span>
          <Send className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

function AnalyticsCard() {
  return (
    <div
      data-testid="preview-analytics"
      className="w-[260px] rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-xl shadow-[0_24px_60px_-12px_rgba(15,23,42,0.2)] p-4"
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.14em]">Treatment efficiency</p>
        <span className="text-[10px] font-bold text-emerald-500 bg-emerald-500/10 rounded-full px-2 py-0.5">+18%</span>
      </div>
      <svg viewBox="0 0 220 70" className="w-full mt-3">
        <defs>
          <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="sparkStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2563EB" />
            <stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
        <path d="M0 58 C 25 52, 40 44, 60 46 S 95 30, 115 32 S 155 14, 180 16 S 210 6, 220 4 L 220 70 L 0 70 Z" fill="url(#sparkFill)" />
        <motion.path
          d="M0 58 C 25 52, 40 44, 60 46 S 95 30, 115 32 S 155 14, 180 16 S 210 6, 220 4"
          fill="none"
          stroke="url(#sparkStroke)"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
        />
      </svg>
      <div className="flex justify-between text-[9px] text-slate-400 mt-2">
        <span>Mar</span><span>Apr</span><span>May</span><span>Jun</span><span>Jul</span>
      </div>
    </div>
  );
}

function PatientProfileCard() {
  return (
    <div
      data-testid="preview-patient-profile"
      className="w-[250px] rounded-2xl border border-slate-200/80 bg-white/90 backdrop-blur-xl shadow-[0_24px_60px_-12px_rgba(15,23,42,0.2)] p-4"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#2563EB] flex items-center justify-center text-white text-xs font-bold">EW</span>
        <div>
          <p className="text-xs font-semibold text-[#0F172A]">Emma Wilson</p>
          <p className="text-[9px] text-slate-400">Aligner · Week 18 of 25</p>
        </div>
      </div>
      <div className="mt-3.5">
        <div className="flex justify-between text-[9px] font-medium text-slate-400 mb-1.5">
          <span>Compliance</span><span className="text-emerald-500">94%</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "94%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#22C55E]"
          />
        </div>
      </div>
      <div className="flex gap-1.5 mt-3.5">
        {["On track", "Low risk"].map((tag) => (
          <span key={tag} className="text-[9px] font-semibold rounded-full bg-emerald-500/10 text-emerald-600 px-2 py-0.5">{tag}</span>
        ))}
      </div>
    </div>
  );
}

export default function ProductPreview() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const ySlow = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yFast = useTransform(scrollYProgress, [0, 1], [90, -70]);
  const yReverse = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  return (
    <section id="preview" ref={ref} data-testid="preview-section" className="relative py-24 lg:py-36 bg-[#F8FAFC] overflow-hidden">
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[44rem] h-[24rem] rounded-full bg-[#2563EB]/8 blur-[110px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <SectionHeading
          testId="preview-heading"
          eyebrow="Product preview"
          title="Designed to feel invisible"
          sub="Production-ready screens your team will actually love — calm, fast, and quietly intelligent."
        />

        <div className="relative mt-20 grid lg:grid-cols-12 gap-10 items-center">
          {/* Dashboard screen */}
          <Reveal className="lg:col-span-7" delay={0.1}>
            <motion.div style={{ y: ySlow }} className="rounded-2xl border border-slate-200/80 bg-white shadow-[0_40px_90px_-20px_rgba(15,23,42,0.22)] overflow-hidden" data-testid="preview-dashboard">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/80">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <span className="text-[10px] text-slate-400 ml-3">app.alignexa.ai — Doctor Dashboard</span>
              </div>
              <div className="grid grid-cols-[52px_1fr]">
                <div className="border-r border-slate-100 py-4 flex flex-col items-center gap-4">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <span key={n} className={`w-8 h-8 rounded-xl ${n === 0 ? "bg-[#2563EB]/10" : "bg-slate-50"}`} />
                  ))}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="font-display font-medium text-sm text-[#0F172A]">Practice Analytics</p>
                      <p className="text-[10px] text-slate-400">Last 30 days · All providers</p>
                    </div>
                    <span className="text-[9px] font-semibold text-[#2563EB] bg-[#2563EB]/10 rounded-full px-2.5 py-1">Live</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5 mb-4">
                    {[
                      { label: "Admin hours saved", value: "41.5h" },
                      { label: "Notes auto-drafted", value: "312" },
                      { label: "No-show rate", value: "2.1%" },
                    ].map((kpi) => (
                      <div key={kpi.label} className="rounded-xl bg-slate-50 p-3">
                        <p className="text-[9px] text-slate-400">{kpi.label}</p>
                        <p className="font-display font-semibold text-lg text-[#0F172A] mt-1">{kpi.value}</p>
                      </div>
                    ))}
                  </div>
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                  >
                    <svg viewBox="0 0 400 90" className="w-full">
                      <defs>
                        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366F1" />
                          <stop offset="100%" stopColor="#2563EB" />
                        </linearGradient>
                      </defs>
                      {[38, 52, 44, 66, 58, 74, 62, 82, 70, 88, 78, 92].map((h, i) => (
                        <rect
                          key={i}
                          x={i * 34 + 4}
                          width="18"
                          rx="5"
                          fill="url(#barGrad)"
                          height={h * 0.85}
                          y={90 - h * 0.85}
                        />
                      ))}
                    </svg>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </Reveal>

          {/* Right column: phone + copilot */}
          <div className="lg:col-span-5 flex flex-col items-center gap-8">
            <motion.div style={{ y: yFast }}>
              <PhoneMockup />
            </motion.div>
          </div>
        </div>

        {/* Floating row of small screens */}
        <div className="relative mt-14 flex flex-wrap justify-center gap-8">
          <motion.div style={{ y: yReverse }}>
            <CopilotCard />
          </motion.div>
          <motion.div style={{ y: ySlow }}>
            <AnalyticsCard />
          </motion.div>
          <motion.div style={{ y: yFast }}>
            <PatientProfileCard />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
